import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  LayoutDashboard,
  Database,
  Scale,
  Settings,
  Radio,
  Clock,
  ChevronLeft,
  CloudBackup,
  LogOut,
  Users,
  Menu, // New icon for mobile
  X,
  ShoppingCart,
} from "lucide-react";
import Dashboard from "./pages/Dashboard";
import DatabaseManagement from "./pages/DataBaseManagement";
import WeighingRecords from "./pages/WeighingRecords";
import FirmwarePage from "./pages/FirmwarePage";
import SettingsPage from "./pages/SettingPage";
import Monitor from "./pages/Monitor.tsx";
import Login from "./pages/Login";
import UserManagement from "./pages/UserManagement";
import SaleModeWeb from "./pages/Sales";

// 1. Define Nav Items outside so both Sidebar and Header can use them
const NAV_ITEMS = [
  {
    id: "dashboard",
    label: "דשבורד",
    path: "/dashboard",
    icon: LayoutDashboard,
  },
  { id: "monitor", label: "מוניטור חי", path: "/monitor", icon: Radio },
  { id: "sales", label: "מכירות", path: "/sales", icon: ShoppingCart },

  { id: "database", label: "ניהול האתר", path: "/database", icon: Database },
  {
    id: "weighings",
    label: "רשומות/דוחות",
    path: "/weighings",
    icon: Scale,
  },
  // { id: "reports", label: "דוחות", path: "/reports", icon: FileBarChart },
  {
    id: "firmware",
    label: "עדכוני קושחה",
    path: "/firmware",
    icon: CloudBackup,
  },
  {
    id: "users",
    label: "ניהול משתמשים",
    path: "/users",
    icon: Users,
    roles: ["admin", "technician"], // We add a metadata field for filtering
  },
  { id: "settings", label: "הגדרות", path: "/settings", icon: Settings },
];

// 2. Main Entry Point
const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("userToken")
  );

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("userData");
    window.history.replaceState(null, "", "/TagWeigh");
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return <Login onLoginSuccess={() => setIsAuthenticated(true)} />;
  }
  return (
    <Router basename="/">
      <Layout onLogout={handleLogout} />
    </Router>
  );
};

// 3. Layout Component handles the logic (useLocation works here because it's inside Router)
const Layout = ({ onLogout }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isMenuOpen, setIsMenuOpen] = useState(false); // Mobile menu state
  const location = useLocation();
  const [deviceStats, setDeviceStats] = useState({
    online: 0,
    offline: 0,
  });
  const navigate = useNavigate();
  const fetchDeviceStats = async () => {
    try {
      const res = await fetch(
        `https://db2sg.com/tagweigh/api/sites?t=${new Date()}`
      );
      const data = await res.json();

      const isOnline = (hb) => {
        if (!hb) return false;
        const time = new Date(hb.replace(" ", "T") + "Z");
        return (Date.now() - time.getTime()) / 1000 <= 60;
      };

      const online = data.filter((s) => isOnline(s.last_heartbeat)).length;
      const offline = data.length - online;

      setDeviceStats({ online, offline });
    } catch (err) {
      console.log("Failed header stats", err);
    }
  };
  useEffect(() => {
    fetchDeviceStats(); // first load

    const interval = setInterval(() => {
      fetchDeviceStats();
    }, 10000); // update every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Logic to find current Page Title
  const getCurrentTitle = () => {
    const currentPath =
      location.pathname === "/" ? "/dashboard" : location.pathname;
    const activeItem = NAV_ITEMS.find((item) => item.path === currentPath);
    return activeItem ? activeItem.label : "דשבורד";
  };
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      className="flex min-h-screen bg-[#F8FAFC] font-sans text-slate-900"
      dir="ltr"
      // dir="rtl"
    >
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] lg:hidden backdrop-blur-sm"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`
        fixed inset-y-0 right-0 w-72 bg-[#0F172A] z-[70] flex flex-col h-screen transition-transform duration-300 ease-in-out
        ${isMenuOpen ? "translate-x-0" : "translate-x-full"} 
        lg:translate-x-0 lg:border-l lg:border-white/5
      `}
      >
        {/* The scrollable nav content */}
        <div className="flex-1 overflow-hidden">
          <button
            className="lg:hidden absolute left-4 top-6 text-slate-400"
            onClick={() => setIsMenuOpen(false)}
          >
            <X size={24} />
          </button>
          <SidebarContent currentPath={location.pathname} />
        </div>

        {/* Fixed Logout Button at bottom - flex-shrink-0 ensures it stays visible */}
        <div className="p-4 border-t border-white/5 flex-shrink-0 bg-[#0F172A]">
          <button
            onClick={onLogout}
            className="w-full p-4 text-red-400 hover:bg-red-500/10 rounded-2xl flex items-center gap-3 transition-colors"
          >
            <LogOut size={20} />
            <span className="font-bold">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Wrapper */}
      <div className="flex-1 flex flex-col min-w-0 lg:mr-72">
        {/* Header */}
        <header className="sticky top-0 bg-white/70 backdrop-blur-xl border-b border-slate-200/60 z-40 px-4 lg:px-10 py-4 flex justify-between items-center">
          {/* Mobile Menu Toggle Button */}
          <button
            onClick={() => setIsMenuOpen(true)}
            className="lg:hidden p-2 bg-slate-100 rounded-xl text-slate-600"
          >
            <Menu size={24} />
          </button>

          {/* Center Title (Hidden on very small screens or adjusted) */}
          <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <h1 className="text-xl lg:text-2xl font-black text-slate-800 tracking-tight">
              {getCurrentTitle()}
            </h1>
          </div>

          {/* Right Side: Clock */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-slate-600 font-mono text-xs lg:text-sm bg-white px-3 py-2 rounded-xl border border-slate-200 shadow-sm">
              <Clock size={16} className="text-emerald-500 hidden sm:block" />
              <span className="font-bold">
                {currentTime.toLocaleString("he-IL")}
              </span>
            </div>
          </div>
          <div
            key={deviceStats.online + deviceStats.offline}
            className="flex items-center gap-3"
          >
            {/* Online */}
            <div
              onClick={() =>
                navigate("/monitor", { state: { status: "online" } })
              }
              className="flex cursor-pointer items-center gap-2 bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-200"
            >
              {/* <Activity size={16} className="text-emerald-600" /> */}
              <div className="flex  flex-col leading-none">
                <span className="text-lg text-center font-black text-emerald-700">
                  {deviceStats.online}
                </span>
                <span className="text-[8px] font-bold text-emerald-500 uppercase tracking-widest">
                  Online Sites
                </span>
              </div>
            </div>

            {/* Offline */}
            <div
              onClick={() =>
                navigate("/monitor", { state: { status: "offline" } })
              }
              className="flex cursor-pointer items-center gap-2 bg-red-50 px-4 py-2 rounded-xl border border-red-200"
            >
              {/* <AlertCircle size={16} className="text-red-600" /> */}
              <div className="flex flex-col leading-none">
                <span className="text-lg text-center font-black text-red-700">
                  {deviceStats.offline}
                </span>
                <span className="text-[8px] font-bold text-red-500 uppercase tracking-widest">
                  Offline Sites
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        {/* Adjusted padding and max-width for mobile */}
        <main className="p-4 lg:p-10 w-full overflow-x-hidden">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/monitor" element={<Monitor />} />
            <Route path="/sales" element={<SaleModeWeb />} />
            <Route path="/database" element={<DatabaseManagement />} />
            <Route path="/weighings" element={<WeighingRecords />} />
            <Route path="/users" element={<UserManagement />} />
            <Route path="/firmware" element={<FirmwarePage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

// 4. Sidebar Content Component
const SidebarContent = ({ currentPath }) => {
  const userRole = localStorage.getItem("role");
  return (
    // Use h-full and flex-col to manage vertical space
    <div className="flex flex-col h-full">
      {/* Brand Section - flex-shrink-0 prevents it from squishing */}
      <div className="px-9 pt-6 mb-4 flex-shrink-0">
        <div className="flex items-center gap-4 group">
          <div className="bg-gradient-to-br p-0 rounded-2xl shadow-xl shadow-emerald-500/20 group-hover:scale-110 transition-transform duration-300 text-white">
            <img src="./logo.jpg" width={50} height={50} alt="Logo" />
          </div>
          <div>
            <h1 className="text-xl font-black text-white tracking-tighter">
              TAGWEIGH
            </h1>
            <span className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest leading-none">
              Version 2.2
            </span>
          </div>
        </div>
      </div>

      {/* Navigation Items - overflow-y-auto makes this part scrollable if height is small */}
      <nav className="flex-1 px-4 space-y-1.5 overflow-y-auto custom-scrollbar pb-4">
        {NAV_ITEMS.filter((item) => {
          if (item.roles) return item.roles.includes(userRole);
          return true;
        }).map((item) => {
          const isActive =
            item.path === currentPath ||
            (currentPath === "/" && item.id === "dashboard");

          return (
            <Link
              key={item.id}
              to={item.path}
              className={`flex items-center justify-between px-4 py-3.5 rounded-2xl transition-all duration-300 group relative ${
                isActive
                  ? "bg-emerald-500/10 text-emerald-400"
                  : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`}
            >
              <div className="flex items-center gap-3 z-10">
                <item.icon
                  size={20}
                  className={
                    isActive
                      ? "text-emerald-400"
                      : "group-hover:text-emerald-400 transition-colors"
                  }
                />
                <span className="font-bold text-[14px]">{item.label}</span>
              </div>

              {isActive && (
                <>
                  <div className="absolute inset-0 bg-emerald-500/5 rounded-2xl border border-emerald-500/20" />
                  <ChevronLeft size={14} className="animate-pulse" />
                </>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default App;
